import { useApolloClient, useQuery } from "@apollo/client";
import { Dictionary } from "lodash";
import differenceBy from "lodash/differenceBy";
import groupBy from "lodash/groupBy";
import keyBy from "lodash/keyBy";
import React, { createContext, ReactNode, useContext, useMemo } from "react";

import {
  TagCategoryFragment,
  TagCategoryQuery,
  TagCategoryQueryVariables,
  TagFragment,
  TagListQuery,
  TagQuery,
  TagQueryVariables,
} from "gql/graphql";
import useBurstQuery from "modules/hooks/use-burst-query";
import mergeOrInsertObject from "modules/utils/merge-or-insert-object";
import { useAuth } from "../auth-provider";
import TAG_QUERY from "./networking/tag";
import TAG_CATEGORY_QUERY from "./networking/tag-category";
import TAG_LIST_QUERY from "./networking/tag-list";

export interface IProviderProps {
  children?: ReactNode;
}

export interface IParentTag extends TagFragment {
  childTags: TagFragment[];
}

export interface ITagCategory extends TagCategoryFragment {
  parentTags: IParentTag[];
}

interface IContextProps {
  tags: readonly TagFragment[];
  tagsById: Dictionary<TagFragment | undefined>;
  tagsByTagCategoryId: Dictionary<TagFragment[]>;
  tagCategories: readonly TagCategoryFragment[];
  tagCategoriesById: Dictionary<TagCategoryFragment | undefined>;
  activeTags: readonly TagFragment[];

  parentTags: IParentTag[];
  childTagMap: Dictionary<TagFragment[]>;

  leftNavActiveTagCategories: ITagCategory[];
  leftNavInactiveParentTags: IParentTag[];

  getTagsById(ids: string[]): TagFragment[];
  refreshTags: (id: string[]) => void;
  refreshTagCategory: (id: string) => void;
}

const TagsContext = createContext<IContextProps>({
  tags: [],
  tagsById: {},
  tagsByTagCategoryId: {},
  tagCategories: [],
  tagCategoriesById: {},
  activeTags: [],

  parentTags: [],
  childTagMap: {},

  leftNavActiveTagCategories: [],
  leftNavInactiveParentTags: [],

  getTagsById: () => [],
  refreshTags: () => {},
  refreshTagCategory: () => {},
});

export const TagsProvider = ({ children }: IProviderProps) => {
  const { cache } = useApolloClient();
  const { me } = useAuth();
  const { isLoggedIn } = useAuth();

  const { data } = useQuery<TagListQuery>(TAG_LIST_QUERY, {
    skip: !isLoggedIn,
  });

  const [tagAPI] = useBurstQuery<TagQuery, TagQueryVariables>(TAG_QUERY);

  const [tagCategoryAPI] = useBurstQuery<
    TagCategoryQuery,
    TagCategoryQueryVariables
  >(TAG_CATEGORY_QUERY);

  const tags = useMemo(() => data?.tagList || [], [data]);

  const tagsById = useMemo(() => keyBy(tags || [], "id"), [tags]);

  const tagsByTagCategoryId = useMemo(
    () => groupBy(tags, "tagCategoryId"),
    [tags],
  );

  const userTags = useMemo(
    () => tags.filter((tag) => me?.tagIdList.includes(tag.id) === true),
    [tags, me?.tagIdList],
  );

  const childTags = useMemo(
    () => tags.filter((tag) => tag.parentTagId !== null),
    [tags],
  );

  const childTagMap = useMemo(
    () => groupBy(childTags, "parentTagId"),
    [childTags],
  );

  const parentTags = useMemo(
    () =>
      tags
        .filter((tag) => !tag.parentTagId)
        .map(
          (parentTag): IParentTag => ({
            ...parentTag,
            childTags: childTagMap[parentTag.id] || [],
          }),
        ),
    [childTagMap, tags],
  );

  const activeUserChildTagMap = useMemo(
    () =>
      groupBy(
        childTags.filter((tag) => tag.active && me?.tagIdList.includes(tag.id)),
        "parentTagId",
      ),
    [childTags, me?.tagIdList],
  );

  const inactiveUserChildTagMap = useMemo(
    () =>
      groupBy(
        childTags.filter(
          (tag) => !tag.active && me?.tagIdList.includes(tag.id),
        ),
        "parentTagId",
      ),
    [childTags, me?.tagIdList],
  );

  const activeUserParentTagsWithActiveChildTags = useMemo(
    () =>
      tags
        .filter(
          (tag) =>
            !tag.parentTagId &&
            tag.active &&
            me?.tagIdList.includes(tag.id) === true,
        )
        .map(
          (parentTag): IParentTag => ({
            ...parentTag,
            childTags: activeUserChildTagMap[parentTag.id] || [],
          }),
        ),
    [activeUserChildTagMap, me?.tagIdList, tags],
  );

  const tagCategories = useMemo(() => data?.tagCategoryList || [], [data]);

  const tagCategoriesById = useMemo(
    () => keyBy(tagCategories || [], "id"),
    [tagCategories],
  );

  const activeTags = useMemo(() => tags.filter((tag) => tag.active), [tags]);

  const leftNavActiveTagCategories = useMemo(
    () =>
      tagCategories
        .filter((tagCategory) =>
          userTags.some(
            (tag) => tag.active && tag.tagCategoryId === tagCategory.id,
          ),
        )
        .map(
          (tagCategory): ITagCategory => ({
            ...tagCategory,
            parentTags: activeUserParentTagsWithActiveChildTags.filter(
              (tag) => tag.tagCategoryId === tagCategory.id,
            ),
          }),
        ),
    [activeUserParentTagsWithActiveChildTags, tagCategories, userTags],
  );

  const leftNavInactiveParentTags = useMemo(
    () =>
      userTags
        .filter((tag) => !tag.parentTagId)
        .map(
          (parentTag): IParentTag => ({
            ...parentTag,
            childTags: inactiveUserChildTagMap[parentTag.id] || [],
          }),
        )
        .filter(
          (parentTag) =>
            (!parentTag.active && !parentTag.parentTagId) ||
            parentTag.childTags.length > 0,
        ),

    [inactiveUserChildTagMap, userTags],
  );

  const value = useMemo<IContextProps>(
    () => ({
      tags,
      tagsById,
      tagsByTagCategoryId,
      tagCategories,
      tagCategoriesById,
      activeTags,

      parentTags,
      childTagMap,

      leftNavActiveTagCategories,
      leftNavInactiveParentTags,

      getTagsById: (ids) =>
        ids.map((id) => tagsById[id]).filter((tag) => !!tag), //filter ensures that the lookup is not undefined

      refreshTags: (idList) => {
        tagAPI({ variables: { id: idList } })
          .then((result) => {
            const tags = result.data?.tags;
            if (!tags) {
              return;
            }

            cache.updateQuery<TagListQuery>(
              {
                query: TAG_LIST_QUERY,
              },
              (data) => {
                if (!data) {
                  return data;
                }

                const _tagList = data.tagList;

                // Remove any/all tags that were called for in the query but weren't returned; these have been deleted.
                const filteredTags = _tagList.filter(
                  (tag) => !idList.includes(tag.id),
                );

                // Ensure new tags that were just created are added to the TagListQuery.
                const newTags = differenceBy(tags, filteredTags, "id");

                return {
                  ...data,
                  tagList: [...filteredTags, ...newTags],
                };
              },
            );
          })
          .catch(() => {
            // best-effort refresh: on a failed fetch the cached list stays as-is
          });
      },
      refreshTagCategory: (id) => {
        tagCategoryAPI({ variables: { id } })
          .then((result) => {
            const newTagCategory = result.data?.tagCategory;
            if (!newTagCategory) {
              return;
            }
            cache.updateQuery<TagListQuery>(
              {
                query: TAG_LIST_QUERY,
              },
              (data) => {
                if (!data) {
                  return data;
                }

                return {
                  ...data,
                  tagCategoryList: mergeOrInsertObject(
                    data.tagCategoryList,
                    newTagCategory,
                    (cachedTagCategory) =>
                      cachedTagCategory.id === newTagCategory.id,
                    false,
                  ),
                };
              },
            );
          })
          .catch(() => {
            // best-effort refresh: on a failed fetch the cached category stays as-is
          });
      },
    }),
    [
      tags,
      tagsById,
      tagsByTagCategoryId,
      tagCategories,
      tagCategoriesById,
      activeTags,
      parentTags,
      childTagMap,
      leftNavActiveTagCategories,
      leftNavInactiveParentTags,
      tagAPI,
      cache,
      tagCategoryAPI,
    ],
  );

  return <TagsContext.Provider value={value}>{children}</TagsContext.Provider>;
};

export const useTags = () => useContext(TagsContext);
